import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ALL_DAYS, copyEntry, sessionOf, anchorIdsOf } from '../../services/anchors';
import { MapPin, Settings, Trash2, Plus, X, Check, Clock, Briefcase, Search, AlertTriangle, Lock, Pencil, GripVertical, ArrowUp, ArrowDown, Save } from 'lucide-react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import UploadControls from './UploadControls';
import { apiFetch } from '../../services/apiClient';
import { locationName } from '../../services/locations';
import PatternGrid from '../ui/PatternGrid';
import { cellsToPayload, computePatternWarnings } from '../../services/patternCells';
import { getAllSkills } from '../../services/scheduleService';
import LocationDrawer from './LocationDrawer';
import ClinicGroupsCard from './ClinicGroupsCard';
import { toast } from '../Toast';
import PageHeader from '../ui/PageHeader';
import AddNewButton from '../ui/AddNewButton';
import { ControlBar, SearchBox, EndGroup } from '../ui/ControlBar';

// --- SUB-COMPONENT: Searchable Selection List (for AddLocationModal) ---
const SearchableList = ({ label, items, selected, onToggle, icon: Icon, placeholder, height = "h-32" }) => {
  const [search, setSearch] = useState('');
  const filtered = items.filter(i => i.toLowerCase().includes(search.toLowerCase()));
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Icon className="w-3 h-3 text-slate-400" />
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">{label}</label>
        </div>
        <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{selected.length} Selected</span>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm focus-within:ring-2 focus-within:ring-[var(--primary)]/20 focus-within:border-[var(--primary)] transition-all">
        <div className="flex items-center px-3 py-2 border-b border-slate-100 bg-slate-50/50">
          <Search className="w-3.5 h-3.5 text-slate-400 mr-2" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={placeholder} className="bg-transparent border-none outline-none text-xs w-full font-medium text-slate-600 placeholder:text-slate-300" />
        </div>
        <div className={`${height} overflow-y-auto custom-scrollbar p-1.5 space-y-0.5`}>
          {filtered.length > 0 ? filtered.map(item => {
            const isSelected = selected.includes(item);
            return (
              <button key={item} onClick={() => onToggle(item)} className={`w-full text-left px-3 py-2 rounded-lg text-[11px] font-bold transition-all flex items-center justify-between group ${isSelected ? 'bg-[var(--primary)]/10 text-[var(--primary)]' : 'hover:bg-slate-50 text-slate-600'}`}>
                <span>{item.replace(/^skill_/i, '')}</span>
                {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-slate-300" />}
              </button>
            );
          }) : <div className="py-8 text-center text-[10px] text-slate-300 font-bold uppercase tracking-widest">No matches</div>}
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: Location Removal Modal ---
const LocationRemovalModal = ({ isOpen, onClose, locationId, locationName, onConfirm }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  if (!isOpen) return null;
  const handleAction = async () => {
    setIsProcessing(true);
    await onConfirm(locationId, locationName);
    setIsProcessing(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-sm border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-[var(--primary)]/10 text-[var(--primary)] rounded-2xl flex items-center justify-center mx-auto mb-6"><AlertTriangle className="w-8 h-8" /></div>
          <h3 className="text-xl font-bold text-slate-900 mb-2">Remove Clinic</h3>
          <p className="text-sm text-slate-500 font-medium mb-8">Are you sure you want to remove <span className="font-bold text-slate-900">{locationName}</span>? This action cannot be undone.</p>
          <div className="flex gap-3">
            <Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
            <Button variant="primary" onClick={handleAction} isLoading={isProcessing} className="flex-1">Remove</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- SUB-COMPONENT: Add Location Modal ---
const AddLocationModal = ({ isOpen, onClose, onConfirm, allSkills, shiftTypes, rankLabels = {} }) => {
  const [name, setName] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  // The SAME cell model the clinic drawer edits, so a clinic can be fully
  // configured here -- shift types, lead/support targets, seniority ceiling --
  // instead of being created bare and then set up a second time from scratch.
  const [patternCells, setPatternCells] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const warnings = useMemo(
    () => computePatternWarnings(patternCells, shiftTypes || []),
    [patternCells, shiftTypes]);

  if (!isOpen) return null;

  const toggleSkill = (skill) => {
    if (selectedSkills.includes(skill)) setSelectedSkills(selectedSkills.filter(s => s !== skill));
    else setSelectedSkills([...selectedSkills, skill]);
  };

  const handleAction = async () => {
    if (!name.trim()) return;
    setIsProcessing(true);
    setError(null);
    const { pattern, requirements } = cellsToPayload(patternCells);
    const result = await onConfirm({
      name: name.trim(), required_skills: selectedSkills, pattern, requirements,
    });
    setIsProcessing(false);
    if (result && result.success === false) {
      setError(result.error || 'The clinic could not be created.');
      return;
    }
    setName(''); setSelectedSkills([]); setPatternCells({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-[#042f2e]/40 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-[24px] shadow-2xl w-full max-w-lg border border-slate-200 overflow-hidden animate-in zoom-in-95 duration-200">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8"><h3 className="text-xl font-bold text-slate-900 tracking-tight">New Clinic Setup</h3><button onClick={onClose} className="p-2 hover:bg-slate-50 rounded-full text-slate-400 transition-colors"><X className="w-5 h-5" /></button></div>
          <div className="space-y-6 mb-10">
            {error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="text-[10px] font-bold text-rose-700">{error}</p>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Clinic / Ward Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. NUH Clinic A" className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[var(--primary)]/20 outline-none transition-all text-sm font-bold text-slate-700 shadow-sm" />
            </div>
            <div className="max-h-[380px] overflow-y-auto pr-2 space-y-8 custom-scrollbar">
              <SearchableList label="Required Skills" items={allSkills} selected={selectedSkills} onToggle={toggleSkill} icon={Briefcase} placeholder="Search skills..." height="h-28" />
              <div>
                <div className="flex items-center gap-2 mb-4 ml-1">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Operating Pattern &amp; Staffing</label>
                </div>
                <PatternGrid
                  shiftTypes={shiftTypes || []}
                  cells={patternCells}
                  onChange={setPatternCells}
                  rankLabels={rankLabels}
                />
                {warnings.length > 0 && (
                  <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 space-y-1">
                    {warnings.map(w => (
                      <p key={w} className="text-[10px] font-bold text-amber-700">{w}</p>
                    ))}
                  </div>
                )}
                {Object.keys(patternCells).length === 0 && (shiftTypes || []).length > 0 && (
                  <p className="mt-3 text-[10px] font-bold text-slate-400 ml-1">
                    Leave every day off to create the clinic empty — it will not roster
                    anybody until a pattern is set.
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-4"><Button variant="secondary" onClick={onClose} className="flex-1">Cancel</Button><Button onClick={handleAction} disabled={isProcessing || !name.trim()} isLoading={isProcessing} className="flex-1" icon={Plus} size="lg">Create Clinic</Button></div>
        </div>
      </div>
    </div>
  );
};

// --- MAIN TAB COMPONENT ---
const shiftTypeCode = (type) =>
  String(type?.shortCode ?? type?.short_code ?? type?.id ?? '').trim().toUpperCase();

const OperationsTab = ({ schedule, loading, solving, handleFileUpload, downloadTemplate, fileInputRef, getEmployeeColor, loadDemoData }) => {
  const [removeModal, setRemoveModal] = useState({ isOpen: false, name: '' });

  // Active shift types from the schedule payload, ordered by start time so the
  // operating grid rows read chronologically (AM, PM, then any evening/night).
  const allShiftTypes = useMemo(() => {
    const list = schedule?.shiftTypes || schedule?.shift_types || [];
    return [...list]
      .filter(t => (t?.isActive ?? t?.is_active) !== false)
      .sort((a, b) => {
        const as = String(a?.startTime ?? a?.start_time ?? '99:99');
        const bs = String(b?.startTime ?? b?.start_time ?? '99:99');
        return as.localeCompare(bs) || shiftTypeCode(a).localeCompare(shiftTypeCode(b));
      });
  }, [schedule]);
  const [addLocModal, setAddLocModal] = useState(false);
  const [drawer, setDrawer] = useState({ isOpen: false, location: null });
  const [locQuery, setLocQuery] = useState('');

  // --- Clinic ORDER editing ---------------------------------------------
  // schedule.locations is the user-defined display order that every view in
  // the app follows (see services/locationOrder.js). The pencil button opens a
  // reorder mode: the clinics become draggable bubbles (with ↑/↓ buttons as a
  // keyboard/touch fallback), the card grid below previews the draft order, and
  // Save persists it with PUT /api/locations/order before the usual refresh.
  const [reorderMode, setReorderMode] = useState(false);
  const [draftOrder, setDraftOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const startReorder = () => {
    setDraftOrder([...(schedule?.locations || [])]);
    setOrderError('');
    setLocQuery('');
    setReorderMode(true);
  };
  const cancelReorder = () => { setReorderMode(false); setDragIdx(null); setOverIdx(null); setOrderError(''); };
  // Arrows: swap-style step to an adjacent position.
  const moveLocation = (from, to) => {
    if (from === to || from < 0 || to < 0 || from >= draftOrder.length || to >= draftOrder.length) return;
    setDraftOrder(prev => {
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };
  // Drag-and-drop: INSERT the dragged card immediately BEFORE the card it is
  // dropped on (the intuitive reading of "put it here"), whichever direction
  // it travelled. Dropping on the trailing end-zone moves it to the end.
  const insertBefore = (from, targetIdx) => {
    if (from < 0 || targetIdx < 0 || from === targetIdx) return;
    setDraftOrder(prev => {
      if (from >= prev.length || targetIdx > prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(from < targetIdx ? targetIdx - 1 : targetIdx, 0, item);
      return next;
    });
  };
  const moveToEnd = (from) => {
    if (from < 0) return;
    setDraftOrder(prev => {
      if (from >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.push(item);
      return next;
    });
  };

  // Auto-scroll the page while a card is being dragged near the top or bottom
  // edge of the scrolling pane. The app's content scrolls inside an inner
  // `overflow-y-auto` div (App.js), not the window, so the browser's native
  // edge-scroll never engages; we drive it ourselves from the last known
  // pointer position, with speed proportional to how close to the edge it is.
  const gridRef = useRef(null);
  const dragYRef = useRef(null);
  useEffect(() => {
    if (!reorderMode || dragIdx === null) return undefined;
    const findScrollParent = (el) => {
      let node = el && el.parentElement;
      while (node && node !== document.body) {
        const { overflowY } = window.getComputedStyle(node);
        if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) return node;
        node = node.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };
    const pane = findScrollParent(gridRef.current);
    const onDragOver = (e) => { dragYRef.current = e.clientY; };
    document.addEventListener('dragover', onDragOver);
    let raf = 0;
    const EDGE = 96, MAX_STEP = 22;
    const tick = () => {
      const y = dragYRef.current;
      if (y !== null && pane) {
        const rect = pane === document.scrollingElement || pane === document.documentElement
          ? { top: 0, bottom: window.innerHeight }
          : pane.getBoundingClientRect();
        if (y < rect.top + EDGE) {
          pane.scrollTop -= Math.ceil(MAX_STEP * (1 - Math.max(0, y - rect.top) / EDGE));
        } else if (y > rect.bottom - EDGE) {
          pane.scrollTop += Math.ceil(MAX_STEP * (1 - Math.max(0, rect.bottom - y) / EDGE));
        }
      }
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      window.cancelAnimationFrame(raf);
      dragYRef.current = null;
    };
  }, [reorderMode, dragIdx]);
  const saveOrder = async () => {
    setSavingOrder(true); setOrderError('');
    try {
      const response = await apiFetch('/api/locations/order', {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order: draftOrder }),
      });
      const body = await response.json().catch(() => ({}));
      if (response.ok && body && body.success) {
        setReorderMode(false); setDragIdx(null);
        loadDemoData('SMALL');
      } else {
        setOrderError((body && body.error) || 'Could not save the clinic order.');
      }
    } catch (err) {
      console.error(err);
      setOrderError('Could not save the clinic order.');
    } finally {
      setSavingOrder(false);
    }
  };

  const allSkills = getAllSkills(schedule);

  /**
   * Read the reason out of a refused request.
   *
   * `response.ok` alone is not enough: the location endpoints used to answer a
   * refusal with 200 {"success": false}, so a delete of a clinic that was
   * already gone refreshed the page and said nothing. Both signals are checked
   * here so the handlers stay correct whichever way the server answers.
   */
  const refusalReason = async (response) => {
    const body = await response.json().catch(() => null);
    if (!response.ok) return (body && (body.detail || body.error)) || `Request failed (${response.status}).`;
    if (body && body.success === false) return (body.detail || body.error) || 'That change could not be applied.';
    return null;
  };

  // Every location call is by the clinic's id; its name is only shown.
  const handleConfirmRemove = async (id, name) => {
    try {
      const response = await apiFetch(`/api/locations/${encodeURIComponent(id)}`, { method: 'DELETE' });
      const reason = await refusalReason(response);
      if (reason) { toast.error(reason); return; }
      toast.success(`${name} removed.`);
      loadDemoData('SMALL');
    } catch (err) { console.error(err); toast.error('Could not reach the server.'); }
  };

  const handleUpdateSettings = async (id, newName, skills, locationType, site) => {
    const oldName = locationName(schedule, id);
    try {
      // location_type and site are sent only when changed ('' clears either).
      const body = { new_name: newName, required_skills: skills,
                     ...(locationType !== undefined ? { location_type: locationType } : {}),
                     ...(site !== undefined ? { site } : {}) };
      const response = await apiFetch(`/api/locations/${encodeURIComponent(id)}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const reason = await refusalReason(response);
      if (reason) { toast.error(reason); return; }
      if (newName && newName !== oldName) toast.success(`Renamed to ${newName}.`);
      await loadDemoData('SMALL');
      if (drawer.location && drawer.location.id === id) {
        setDrawer({ ...drawer, location: { ...drawer.location, name: newName, currentSkills: skills } });
      }
    } catch (err) { console.error(err); toast.error('Could not reach the server.'); }
  };

  const handleUpdateBulkAnchors = async ({ location, full_prefs, roles }) => {
    try {
      // One request per tie added or removed, by staff id. The session comes
      // from the fixed key table (services/anchors.js), never from splitting.
      const originalPrefs = copyEntry((schedule.preferred_assignments || schedule.preferredAssignments || {})[location]);
      const wanted = copyEntry(full_prefs);
      const allKeys = new Set([...Object.keys(originalPrefs), ...Object.keys(wanted)]);
      const send = (key, staffId, action) => {
        const session = sessionOf(key);
        return apiFetch('/api/locations/preferences/granular', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location, staff_id: staffId, day_idx: session ? session.day : null,
                                 time_block: session ? session.half : null, action }),
        });
      };

      for (const key of allKeys) {
        const before = originalPrefs[key] || [];
        const after = wanted[key] || [];
        for (const id of after) if (!before.includes(id)) await send(key, id, 'add');
        for (const id of before) if (!after.includes(id)) await send(key, id, 'remove');
      }
      // Anchor roles, AFTER the ties above exist (a role is refused for somebody
      // who is not an anchor) and before the one refresh below.
      const anchored = anchorIdsOf(wanted);
      const roleBody = {};
      anchored.forEach(id => { roleBody[id] = (roles || {})[id] || 'either'; });
      if (Object.keys(roleBody).length) {
        await apiFetch('/api/locations/anchor-roles', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location, roles: roleBody }),
        });
      }
      await loadDemoData('SMALL');
    } catch (err) { console.error(err); }
  };

  const handleConfirmAddFullSetup = async (setupData) => {
    try {
      const response = await apiFetch('/api/locations/full-setup', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(setupData),
      });
      const body = await response.json().catch(() => null);
      // Report the outcome back so the modal can show WHY a create failed and
      // keep the operator's input, instead of closing and losing it.
      if (!response.ok || body?.success === false) {
        return { success: false, error: body?.error || `Create failed (HTTP ${response.status}).` };
      }
      await loadDemoData('SMALL');
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  // Sends the state the planner is asking for, so a double-click or a retry
  // repeats it rather than flipping it back.
  const toggleLocationActive = async (id) => {
    const active = (schedule?.inactiveLocations || schedule?.inactive_locations || []).includes(id);
    try {
      const response = await apiFetch(`/api/locations/${encodeURIComponent(id)}/toggle-active`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => null);
        toast.error(body?.detail || `Could not change the clinic (HTTP ${response.status}).`);
        return;
      }
      await loadDemoData('SMALL');
    } catch (err) { console.error(err); toast.error('Could not reach the server.'); }
  };

  if (!schedule) return null;
  const inactiveList = schedule.inactiveLocations || schedule.inactive_locations || [];
  // Optional OT / Clinic tag per location (absent for ED and IP).
  const locationTypes = schedule?.locationTypes || schedule?.location_types || {};
  const locationSites = schedule?.locationSites || schedule?.location_sites || {};

  // NO alphabetical sort: schedule.locations IS the user-defined order. In
  // reorder mode the grid previews the draft so the effect of a drag is visible.
  const orderedLocations = reorderMode ? draftOrder : (schedule.locations || []);
  const filteredLocations = orderedLocations.filter(l => locationName(schedule, l).toLowerCase().includes(locQuery.toLowerCase()));

  return (
    <div className="animate-in fade-in space-y-6">
      <LocationRemovalModal isOpen={removeModal.isOpen} locationId={removeModal.id} locationName={removeModal.name} onClose={() => setRemoveModal({ isOpen: false, id: null, name: '' })} onConfirm={handleConfirmRemove} />
      <AddLocationModal isOpen={addLocModal} allSkills={allSkills} shiftTypes={allShiftTypes} rankLabels={schedule.clinicalRankLabels || schedule.clinical_rank_labels || {}} onClose={() => setAddLocModal(false)} onConfirm={handleConfirmAddFullSetup} />
      
      {drawer.isOpen && drawer.location && (
        <LocationDrawer 
          isOpen={drawer.isOpen}
          location={drawer.location}
          allSkills={allSkills}
          employees={schedule.employees || []}
          preferences={(schedule.preferred_assignments || schedule.preferredAssignments || {})[drawer.location.id]}
          isActive={!inactiveList.includes(drawer.location.id)}
          getEmployeeColor={getEmployeeColor}
          onClose={() => setDrawer({ isOpen: false, location: null })}
          onUpdateSettings={handleUpdateSettings}
          onToggleActive={toggleLocationActive}
          onUpdateAnchors={handleUpdateBulkAnchors}
          locationType={locationTypes[drawer.location.id] || ''}
          locationSite={locationSites[drawer.location.id] || ''}
          anchorRoles={(schedule?.anchorRoles || schedule?.anchor_roles || {})[drawer.location.id] || {}}
        />
      )}

      {/* FLOATING IDENTITY */}
      <PageHeader icon={MapPin} title="Operations & Clinics" />

      <ControlBar>
        <SearchBox value={locQuery} onChange={setLocQuery} placeholder="Search clinics..." />
        <EndGroup label="Actions">
              {/* Upload + template collapsed into one split control. Adding a
                  clinic is the dashed button under the clinic cards (Oct 2026). */}
              <UploadControls
                templateType="locations"
                downloadTemplate={downloadTemplate}
                handleFileUpload={handleFileUpload}
                fileInputRef={fileInputRef}
                disabled={loading || solving}
                variant="secondary"
              />
              {!reorderMode ? (
                <Button onClick={startReorder} variant="secondary" icon={Pencil} title="Change the order clinics appear in, everywhere in the app" className="h-[36px] font-bold text-xs" disabled={!(schedule?.locations || []).length}>Reorder</Button>
              ) : (
                <>
                  <Button onClick={saveOrder} variant="primary" icon={Save} isLoading={savingOrder} className="h-[36px] px-6 font-bold text-xs">Save Order</Button>
                  <Button onClick={cancelReorder} variant="secondary" icon={X} disabled={savingOrder} className="h-[36px] font-bold text-xs">Cancel</Button>
                </>
              )}
        </EndGroup>
      </ControlBar>

      {/* REORDER MODE hint: the clinic cards themselves are the drag targets. */}
      {reorderMode && (
        <div className="flex items-center justify-between gap-4 px-1 -mt-2">
          <p className="text-xs text-slate-500 font-medium">
            <span className="font-bold text-[var(--primary)] uppercase tracking-widest text-[10px] mr-2">Reordering</span>
            Drag a clinic card and drop it on the clinic whose place it should take, or use the ↑ ↓ arrows. Save to apply this order everywhere in the app.
          </p>
          {orderError && <span className="text-xs font-bold text-rose-600 whitespace-nowrap">{orderError}</span>}
        </div>
      )}

      {/* Active shift types, shared by every card's operating grid. */}
      <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
          {filteredLocations.map(loc => {
              // The operating grid reads location_shift_patterns, NOT the legacy
              // location_schedules. Generation is pattern-driven and the legacy
              // field is no longer written by the drawer, so rendering it here
              // would show hours that are not actually rostered.
              const patternSource = schedule.locationShiftPatterns || schedule.location_shift_patterns || {};
              const locPattern = patternSource[loc] || [];
              const patternDay = (item) => {
                const d = item?.day;
                return typeof d === 'string' ? parseInt(d, 10) : d;
              };
              const patternTypeId = (item) => String(item?.shiftTypeId ?? item?.shift_type_id ?? '');

              // Rows: show ALL active shift types, so any shift (including custom
              // types like ND) can be plotted on any clinic — including a newly
              // created one whose pattern is still empty. Previously only AM/PM
              // (hardcoded baseline) plus types ALREADY used by this location were
              // shown, so a new clinic surfaced only AM/PM and custom types could
              // never be added (they weren't shown as rows to toggle). AM/PM remain
              // present because they are active types in allShiftTypes; ordering is
              // chronological (AM, PM, then evening/night), matching the old layout.
              const rowTypes = allShiftTypes;
              const skillSource = schedule.locationSkills || schedule.location_skills || {};
              const rawSkills = skillSource[loc] || [];
              const reqSkills = Array.isArray(rawSkills) ? rawSkills : [rawSkills];
              
              const prefSource = schedule.preferred_assignments || schedule.preferredAssignments || {};
              const locPrefs = prefSource[loc];
              
              const anchorIds = anchorIdsOf(locPrefs);
              const anchors = (schedule.employees || []).filter(emp => anchorIds.has(emp.id)).sort((a, b) => a.name.localeCompare(b.name));
              const isActive = !inactiveList.includes(loc);

              // Conditional weekend columns: show Sat/Sun only when this clinic actually
              // operates then (day 5/6 present). Keeps weekday-only cards at 5 columns,
              // consistent with the Dashboard's hasWeekendOps gating.
              const locHasWeekend = locPattern.some(item => patternDay(item) >= 5);
              const dayCols = locHasWeekend ? [0, 1, 2, 3, 4, 5, 6] : [0, 1, 2, 3, 4];
              const dayLabels = locHasWeekend
                ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
                : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
              
              return (
              <div key={loc} className="relative h-full">
              {/* Insertion indicator: a bar in the grid gap immediately BEFORE
                  the hovered card, i.e. exactly where the dragged card will
                  land. Lives on this wrapper because the Card clips overflow. */}
              {reorderMode && overIdx === draftOrder.indexOf(loc) && dragIdx !== draftOrder.indexOf(loc) && (
                <div className="absolute top-1 bottom-1 -left-[15px] w-1.5 rounded-full bg-[var(--primary)] shadow-[0_0_0_3px_rgba(194,65,12,0.15)] z-30 pointer-events-none" />
              )}
              <Card
                padding="p-0"
                // Reorder mode: the card is draggable and a drop target. Two
                // HTML5 drag-and-drop traps are handled here: (1) the dragged
                // node must NOT be restyled synchronously inside dragstart or
                // Chrome cancels the drag, so the "lifted" state is set on the
                // next tick; (2) Firefox needs setData() or the drag never starts.
                draggable={reorderMode}
                onDragStart={reorderMode ? (e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', loc);
                  const from = draftOrder.indexOf(loc);
                  setTimeout(() => setDragIdx(from), 0);
                } : undefined}
                onDragOver={reorderMode ? (e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  const pos = draftOrder.indexOf(loc);
                  if (overIdx !== pos) setOverIdx(pos);
                } : undefined}
                onDrop={reorderMode ? (e) => {
                  e.preventDefault();
                  const dropped = e.dataTransfer.getData('text/plain');
                  const from = dragIdx !== null ? dragIdx : draftOrder.indexOf(dropped);
                  insertBefore(from, draftOrder.indexOf(loc));
                  setDragIdx(null); setOverIdx(null);
                } : undefined}
                onDragEnd={reorderMode ? () => { setDragIdx(null); setOverIdx(null); } : undefined}
                className={`group transition-all p-0 flex flex-col h-full relative overflow-hidden ${!isActive ? 'opacity-60 grayscale-[0.5]' : ''} ${reorderMode ? 'cursor-grab active:cursor-grabbing select-none' : ''} ${reorderMode && dragIdx === draftOrder.indexOf(loc) ? 'opacity-40 border-dashed' : ''}`}
              >
                  {reorderMode && (() => {
                    const pos = draftOrder.indexOf(loc);
                    return (
                      <>
                        {/* Transparent shield: while reordering, the card's own
                            controls (grid toggles, settings, delete) must not
                            react to clicks; the drag itself bubbles to the card. */}
                        <div className="absolute inset-0 z-10 bg-white/30" />
                        {/* Position badge + keyboard/touch fallback arrows. */}
                        <div className="absolute top-3 left-3 z-20 flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-white border border-[var(--primary)]/40 shadow-md" draggable={false} onClick={(e) => e.stopPropagation()}>
                          <GripVertical className="w-3.5 h-3.5 text-slate-300" />
                          <span className="text-[10px] font-black tabular-nums text-[var(--primary)] w-5 text-center">{pos + 1}</span>
                          <button type="button" draggable={false} onClick={(e) => { e.stopPropagation(); moveLocation(pos, pos - 1); }} disabled={pos === 0} title="Move earlier" className="p-1 rounded-full text-slate-400 hover:text-[var(--primary)] hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"><ArrowUp className="w-3 h-3" /></button>
                          <button type="button" draggable={false} onClick={(e) => { e.stopPropagation(); moveLocation(pos, pos + 1); }} disabled={pos === draftOrder.length - 1} title="Move later" className="p-1 rounded-full text-slate-400 hover:text-[var(--primary)] hover:bg-slate-100 disabled:opacity-30 disabled:hover:bg-transparent"><ArrowDown className="w-3 h-3" /></button>
                        </div>
                      </>
                    );
                  })()}
                  <div className="p-6 flex-1">
                      <div className="flex justify-between items-start mb-6">
                          <div><h4 className="font-bold text-slate-900 text-base tracking-tight">{locationName(schedule, loc)}{locationSites[loc] && <span className="ml-2 align-middle px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest bg-teal-50 text-teal-700" title="Site">{locationSites[loc]}</span>}{locationTypes[loc] && <span className={`ml-2 align-middle px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-widest ${locationTypes[loc] === 'OT' ? 'bg-indigo-50 text-indigo-700' : 'bg-slate-100 text-slate-500'}`} title="Location type">{locationTypes[loc]}</span>}</h4><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Clinic ID: {loc}</p></div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="xs" 
                              onClick={() => setDrawer({ isOpen: true, location: { id: loc, name: locationName(schedule, loc), currentSkills: reqSkills } })} 
                              icon={Settings} 
                            />
                            <Button 
                              variant="ghost" 
                              size="xs" 
                              icon={Trash2} 
                              onClick={() => setRemoveModal({ isOpen: true, id: loc, name: locationName(schedule, loc) })} 
                              className="hover:bg-[var(--primary)]/10 hover:text-[var(--primary)]" 
                            />
                          </div>
                      </div>
                      <div className="mb-8">
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] mb-3 px-1">Weekly Operating Grid</p>
                          <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 shadow-inner">
                              <div className="flex w-full gap-1.5 mb-2"><div className="w-8"></div>{dayLabels.map((day, i) => (<div key={day} className={`flex-1 text-center text-[9px] font-bold uppercase ${dayCols[i] >= 5 ? 'text-teal-400' : 'text-slate-300'}`}>{day}</div>))}</div>
                              {rowTypes.length === 0 ? (
                                <div className="py-4 text-center text-[9px] font-bold text-slate-300 uppercase tracking-widest">No shift types defined</div>
                              ) : rowTypes.map((type, ri) => {
                                const code = shiftTypeCode(type);
                                const isExtra = !['AM', 'PM'].includes(code);
                                return (
                                  <div key={type.id} className={`flex w-full gap-1.5 items-center ${ri < rowTypes.length - 1 ? 'mb-1.5' : ''}`}>
                                    <div className={`w-8 text-[9px] font-bold uppercase text-right pr-2 ${isExtra ? 'text-teal-500' : 'text-slate-400'}`}>{code}</div>
                                    {dayCols.map((dayIdx) => {
                                      const io = locPattern.some(item => patternDay(item) === dayIdx && patternTypeId(item) === String(type.id));
                                      return (<div key={dayIdx} className={`flex-1 h-7 rounded-lg border-2 ${io ? 'bg-emerald-50 border-emerald-500 shadow-sm' : `bg-white border-dashed ${dayIdx >= 5 ? 'border-teal-200' : 'border-slate-200'} opacity-40`}`}></div>);
                                    })}
                                  </div>
                                );
                              })}
                          </div>
                      </div>
                      <div className="space-y-5">
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 ml-1">Required Skills</p>
                            <div className="flex flex-wrap gap-1.5">
                              {reqSkills.map((s) => (
                                <span key={s} className="px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[10px] font-bold text-slate-600 shadow-sm transition-all hover:border-slate-200">
                                  {s.replace(/^skill_/i, '')}
                                </span>
                              ))}
                              {reqSkills.length === 0 && <span className="text-[10px] text-slate-300 italic ml-1">No mandatory skills</span>}
                            </div>
                          </div>
                          <div>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2.5 ml-1">Anchored Staff</p>
                            <div className="flex flex-wrap gap-1.5">
                              {anchors.map(e => {
                                // Anchors are staff ids per session key (services/anchors.js);
                                // these compared NAMES, so neither icon ever showed.
                                const isGlobal = (locPrefs?.[ALL_DAYS] || []).includes(e.id);
                                const hasGranular = Object.keys(locPrefs || {}).some(k => k !== ALL_DAYS && (locPrefs[k] || []).includes(e.id));
                                
                                return (
                                  <div key={e.id || e.name} className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 border border-slate-100 rounded-md text-[10px] font-bold text-slate-700 shadow-sm transition-all hover:border-slate-200">
                                    <div className="w-1 h-3 rounded-sm" style={{ backgroundColor: getEmployeeColor(e.name) }} />
                                    {e.name}
                                    <div className="flex items-center gap-0.5 ml-0.5">
                                      {hasGranular && <Clock className="w-2.5 h-2.5 text-teal-500" />}
                                      {isGlobal && <Lock className="w-2.5 h-2.5 text-amber-500" />}
                                    </div>
                                  </div>
                                );
                              })}
                              {anchors.length === 0 && <span className="text-[10px] text-slate-300 italic ml-1">No personnel preferences</span>}
                            </div>
                          </div>
                      </div>
                  </div>
                  
                  <div className="px-6 py-3 border-t border-slate-100 bg-white flex items-center justify-between">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Master Status</span>
                    <div className="flex items-center gap-2"><div className={`w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.4)]' : 'bg-slate-400'}`} /><span className={`text-[10px] font-bold uppercase tracking-tight ${isActive ? 'text-emerald-700' : 'text-slate-500'}`}>{isActive ? 'Operating' : 'Inactive'}</span></div>
                  </div>
              </Card>
              </div>
              );
          })}
          {/* End zone: with insert-before semantics nothing can be dropped
              "after the last card", so this target moves a card to the end. */}
          {reorderMode && (
            <div
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (overIdx !== 'end') setOverIdx('end'); }}
              onDrop={(e) => {
                e.preventDefault();
                const dropped = e.dataTransfer.getData('text/plain');
                const from = dragIdx !== null ? dragIdx : draftOrder.indexOf(dropped);
                moveToEnd(from);
                setDragIdx(null); setOverIdx(null);
              }}
              className={`relative min-h-[160px] rounded-2xl border-2 border-dashed flex items-center justify-center text-center p-6 transition-all ${overIdx === 'end' ? 'border-[var(--primary)] bg-orange-50/60' : 'border-slate-200 bg-slate-50/40'}`}
            >
              {overIdx === 'end' && <div className="absolute top-1 bottom-1 -left-[15px] w-1.5 rounded-full bg-[var(--primary)] shadow-[0_0_0_3px_rgba(194,65,12,0.15)] z-30 pointer-events-none" />}
              <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Drop here to move to the end</span>
            </div>
          )}
      </div>

      {/* Adding, at the bottom of the list like everywhere else (Oct 2026). */}
      {!reorderMode && <AddNewButton label="New clinic" onClick={() => setAddLocModal(true)} />}

      {/* Which clinics the department's rules mean (logic/location_groups.py). */}
      {!reorderMode && <ClinicGroupsCard schedule={schedule} onSaved={() => loadDemoData('SMALL')} />}
    </div>
  );
};

export default OperationsTab;
